import { client } from "./supabase";

// ─── PLAN PAGOS ACTIVOS (cuotas de un activo CapEx planificado) ───────────────
//
// Espejo de planCobros.js (rama "pagos"), pero colgado de activos(id) vía
// plan_pagos_activos.activo_id en vez de facturas_sii_recibidas. Doctrina (ver
// ARQUITECTURA 4.19.1 / 4.2.mm / 10.100): pagar una cuota es CAJA PURA
// (movimiento_bancario_id + fecha_pago_real + estado='pagada'); la activación /
// depreciación del activo va por su FACTURA (activos.fecha_compra), aparte.

export const getPlanPagosActivos = async (activoId) => {
  const { data, error } = await client
    .from("plan_pagos_activos")
    .select("*")
    .eq("activo_id", activoId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

// Set de activo_id que tienen AL MENOS una cuota en el plan (cualquier estado). Necesario aparte de
// "tiene cuotas pendientes": si el plan está completo (todas pagadas), el activo sigue teniendo plan
// y NO debe volver a proyectar la línea única. Lo usa la bifurcación de CapEx en FlujoCajaV2.
export const getActivosConPlan = async (pais) => {
  const { data, error } = await client.from("plan_pagos_activos").select("activo_id").eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.activo_id));
};

// Conteo de cuotas por activo (cualquier estado) para una lista de ids, en una sola query. Alimenta
// el indicador "plan de cuotas (N)" del modal Inversión de Edificios. Devuelve { activo_id: count }.
export const getPlanCountsByActivo = async (activoIds) => {
  if (!activoIds || activoIds.length === 0) return {};
  const { data, error } = await client.from("plan_pagos_activos").select("activo_id").in("activo_id", activoIds);
  if (error) throw error;
  const counts = {};
  for (const row of data || []) counts[row.activo_id] = (counts[row.activo_id] || 0) + 1;
  return counts;
};

export const crearPlanPagosActivos = async (cuotas) => {
  const { data, error } = await client.from("plan_pagos_activos").insert(cuotas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaActivo = async (id, cambios) => {
  const { data, error } = await client.from("plan_pagos_activos").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

// Borra UNA cuota por id, SOLO si está pendiente (el `.eq("estado","pendiente")` es el candado en
// DB). Nunca toca cuotas pagadas — están vinculadas a un movimiento histórico vía movimiento_bancario_id.
// Si la cuota está pagada (o no existe), no borra nada y lanza error. (Espejo de eliminarCuotaPago.)
export const eliminarCuotaActivo = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_activos")
    .delete()
    .eq("id", id)
    .eq("estado", "pendiente")
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar la cuota ${id} (¿ya está pagada?)`);
  return data[0];
};

// Reversión de cuota: la devuelve a 'pendiente' limpiando el vínculo bancario. Es lo que a
// plan_pagos_facturas le FALTA (§2 del informe: desvincular un pago no revierte la cuota, queda
// colgada). Se usará al desvincular una cuota de activo del banco (commit 4).
export const revertirCuotaActivo = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_activos")
    .update({ estado: "pendiente", fecha_pago_real: null, movimiento_bancario_id: null })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ─── Cuotas futuras pendientes (proyección CapEx) ─────────────────────────────
//
// Lee plan_pagos_activos + join al activo (FK real). NO se puede embeber el edificio: activos.destino_id
// es polimórfico (edificio/cartelería/empleado) y no tiene FK a edificios — el nombre del edificio lo
// resuelve el consumidor (FlujoCajaV2, commit 2) vía edificiosMap[destino_id], igual que mapCapex.
// Filtra estado='pendiente' + país; SIN filtro de fecha → incluye vencidas (patrón 10.155). El join
// !inner + eliminado=false descarta cuotas de activos borrados (soft-delete no dispara el CASCADE).
// `activoId` opcional: acota la carga a las cuotas de UN activo (refetch dirigido tras editar su
// plan, en vez de recargar todo el panel). Sin el filtro, comportamiento idéntico al de siempre.
export const getCuotasActivosFuturas = async (pais, { activoId } = {}) => {
  let query = client
    .from("plan_pagos_activos")
    .select(
      `
      *,
      activo:activos!inner(
        id, pais, destino_id, destino_tipo, tipo_activo, moneda, costo_unitario, exento_iva, eliminado, proveedor_id,
        proveedor:proveedores(nombre)
      )
    `
    )
    .eq("estado", "pendiente")
    .eq("pais", pais)
    .eq("activo.eliminado", false);
  if (activoId != null) query = query.eq("activo_id", activoId);
  const { data, error } = await query.order("fecha_vencimiento");
  if (error) throw error;
  const cuotas = data || [];
  if (cuotas.length === 0) return cuotas;

  // Enriquecer con `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado) para el label
  // "cuota N/M". El select de arriba filtra estado='pendiente', así que su conteo daría solo las
  // pendientes (M mal); se cuenta aparte sin filtro de estado. (Espejo de getCuotasPagosFuturas.)
  const activoIds = [...new Set(cuotas.map((c) => c.activo_id).filter((id) => id != null))];
  const { data: todasLasCuotas, error: errTotal } = await client
    .from("plan_pagos_activos")
    .select("activo_id")
    .in("activo_id", activoIds);
  if (errTotal) throw errTotal;
  const totalPorActivo = {};
  for (const row of todasLasCuotas || []) {
    totalPorActivo[row.activo_id] = (totalPorActivo[row.activo_id] || 0) + 1;
  }
  return cuotas.map((c) => ({ ...c, total_cuotas: totalPorActivo[c.activo_id] || 0 }));
};
